import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Loader2, RefreshCw } from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════════════
   1. CHARGEMENT

   Quel indicateur pour quelle attente :
     - on connaît la FORME de ce qui arrive (page, carte, tableau, liste)
       → un SQUELETTE de cette forme : rien ne saute quand ça arrive ;
     - petite opération de durée inconnue → `Chargeur` ;
     - on connaît le POURCENTAGE → `BarreProgression valeur={…}` ;
     - on ne le connaît pas → `BarreProgression` sans valeur ;
     - une zone ne doit plus être touchée pendant l'opération → `Voile` ;
     - des données sont déjà à l'écran et se rafraîchissent →
       `IndicateurActualisation`, JAMAIS un squelette qui les efface.
   Une action (bouton) a son propre état : voir `BoutonAction`.
   ═══════════════════════════════════════════════════════════════════════ */

const etiquette = (texte) => ({ role: 'status', 'aria-live': 'polite', 'aria-label': texte });

/* ── Squelettes ───────────────────────────────────────────────────────── */

export function Squelette({ className = '', style, rond = false }) {
  return <span aria-hidden="true" className={`squelette block ${rond ? '!rounded-full' : ''} ${className}`} style={style} />;
}

/** Des lignes de texte, la dernière plus courte, comme un paragraphe. */
export function SqueletteTexte({ lignes = 3, className = '' }) {
  return (
    <div className={`space-y-2 ${className}`} aria-hidden="true">
      {Array.from({ length: lignes }, (_, i) => (
        <Squelette key={i} className="h-3" style={{ width: i === lignes - 1 && lignes > 1 ? '62%' : '100%' }} />
      ))}
    </div>
  );
}

export function SqueletteImage({ className = 'h-40 w-full' }) {
  return <Squelette className={`!rounded-[var(--r-pastille,12px)] ${className}`} />;
}

/** Une carte ou un widget : titre, chiffre, deux lignes. */
export function SqueletteCarte({ className = '' }) {
  return (
    <div className={`bg-[var(--bg-card)] rounded-[var(--r-carte,22px)] p-5 shadow-[var(--ombre-carte)] ${className}`} {...etiquette('Chargement')}>
      <Squelette className="h-3 w-24 mb-4" />
      <Squelette className="h-7 w-32 mb-4" />
      <SqueletteTexte lignes={2} />
    </div>
  );
}

/** Un tableau : l'en-tête et quelques lignes, aux colonnes du vrai tableau. */
export function SqueletteTableau({ lignes = 5, colonnes = 5, className = '' }) {
  return (
    <div className={`bg-[var(--bg-card)] rounded-[var(--r-carte,22px)] shadow-[var(--ombre-carte)] overflow-hidden ${className}`} {...etiquette('Chargement du tableau')}>
      <div className="flex gap-4 px-5 py-4 border-b border-[var(--border)]">
        {Array.from({ length: colonnes }, (_, c) => <Squelette key={c} className="h-3 flex-1" style={{ maxWidth: c === 0 ? 160 : 110 }} />)}
      </div>
      {Array.from({ length: lignes }, (_, l) => (
        <div key={l} className="flex items-center gap-4 px-5 py-4 border-b border-[var(--border)] last:border-0">
          {Array.from({ length: colonnes }, (_, c) => (
            <div key={c} className="flex-1" style={{ maxWidth: c === 0 ? 220 : 140 }}>
              <Squelette className="h-3" style={{ width: `${55 + ((l * 7 + c * 13) % 40)}%` }} />
              {c === 0 && <Squelette className="h-2.5 mt-2" style={{ width: '45%' }} />}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/** Une liste : pastille, deux lignes, un bouton. */
export function SqueletteListe({ lignes = 4, className = '' }) {
  return (
    <ul className={`bg-[var(--bg-card)] rounded-[var(--r-carte,22px)] shadow-[var(--ombre-carte)] divide-y divide-[var(--border)] ${className}`} {...etiquette('Chargement de la liste')}>
      {Array.from({ length: lignes }, (_, i) => (
        <li key={i} className="flex items-center gap-3 px-5 py-4">
          <Squelette className="w-10 h-10 !rounded-[var(--r-pastille,12px)] shrink-0" />
          <div className="flex-1 min-w-0">
            <Squelette className="h-3 mb-2" style={{ width: `${40 + (i * 17) % 35}%` }} />
            <Squelette className="h-2.5" style={{ width: '30%' }} />
          </div>
          <Squelette className="h-8 w-20 !rounded-full shrink-0" />
        </li>
      ))}
    </ul>
  );
}

/** Un graphique : des barres de hauteurs variées sur un axe. */
export function SqueletteGraphique({ className = 'h-48' }) {
  const hauteurs = [45, 70, 55, 85, 40, 65, 75, 50];
  return (
    <div className={`flex items-end gap-2 ${className}`} {...etiquette('Chargement du graphique')}>
      {hauteurs.map((h, i) => <Squelette key={i} className="flex-1 !rounded-t-md !rounded-b-none" style={{ height: `${h}%` }} />)}
    </div>
  );
}

/** Une page : titre, rangée de chiffres, un bloc de contenu. */
export function SquelettePage({ contenu = 'tableau' }) {
  return (
    <div className="space-y-6" {...etiquette('Chargement de la page')}>
      <div>
        <Squelette className="h-8 w-72 mb-3" />
        <Squelette className="h-3 w-48" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="bg-[var(--bg-card)] rounded-[var(--r-carte,22px)] p-5 shadow-[var(--ombre-carte)]">
            <Squelette className="h-3 w-20 mb-3" /><Squelette className="h-7 w-14" />
          </div>
        ))}
      </div>
      {contenu === 'liste' ? <SqueletteListe /> : contenu === 'cartes'
        ? <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{[0, 1, 2].map((i) => <SqueletteCarte key={i} />)}</div>
        : <SqueletteTableau />}
    </div>
  );
}

/* ── Chargeurs ────────────────────────────────────────────────────────── */

/** Petite opération de durée inconnue. */
export function Chargeur({ taille = 'md', libelle = 'Chargement…', className = '' }) {
  const t = { sm: 'w-4 h-4', md: 'w-6 h-6', lg: 'w-9 h-9' }[taille] || 'w-6 h-6';
  return (
    <span className={`inline-flex ${className}`} {...etiquette(libelle)}>
      <Loader2 className={`${t} animate-spin text-[var(--accent-texte)]`} aria-hidden="true" />
    </span>
  );
}

/** Chargement local, avec ce qu'on attend en clair. */
export function ChargeurLocal({ texte = 'Chargement…', className = '' }) {
  return (
    <p className={`flex items-center justify-center gap-2 py-8 text-[13px] text-[var(--text-muted)] ${className}`} role="status" aria-live="polite">
      <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> {texte}
    </p>
  );
}

/** Progression : déterminée si `valeur` (0–100) est connue, sinon indéterminée. */
export function BarreProgression({ valeur = null, libelle, epaisseur = 6, className = '', ton = 'accent' }) {
  const determinee = typeof valeur === 'number' && Number.isFinite(valeur);
  const pct = determinee ? Math.max(0, Math.min(100, valeur)) : null;
  const couleur = ton === 'alerte' ? 'var(--alerte)' : 'var(--accent)';
  return (
    <div className={className}>
      {(libelle || determinee) && (
        <div className="flex justify-between text-[12px] mb-1.5 text-[var(--text-muted)]">
          <span>{libelle}</span>{determinee && <span className="tabular-nums font-semibold text-[var(--text-bold)]">{Math.round(pct)} %</span>}
        </div>
      )}
      <div className="relative overflow-hidden rounded-full bg-[var(--bg-main)]" style={{ height: epaisseur }}
           role="progressbar" aria-label={libelle || 'Progression'}
           aria-valuemin={0} aria-valuemax={100} aria-valuenow={determinee ? Math.round(pct) : undefined}>
        {determinee
          ? <div className="h-full rounded-full transition-[width] duration-300" style={{ width: `${pct}%`, background: couleur }} />
          : <div className="barre-indeterminee" />}
      </div>
    </div>
  );
}

/** Opération bloquante sur UNE zone : le parent doit être `relative`. */
export function Voile({ actif, texte = 'Traitement en cours…' }) {
  if (!actif) return null;
  return (
    <div className="absolute inset-0 z-20 grid place-items-center rounded-[inherit] backdrop-blur-[1px]" style={{ background: "color-mix(in srgb, var(--bg-card, #fff) 78%, transparent)" }}
         role="status" aria-live="polite">
      <span className="inline-flex items-center gap-2 rounded-full bg-[var(--bg-card)] px-4 py-2 text-[13px] font-semibold text-[var(--text-bold)] shadow-[var(--ombre-2)]">
        <Loader2 className="w-4 h-4 animate-spin text-[var(--accent-texte)]" aria-hidden="true" /> {texte}
      </span>
    </div>
  );
}

/** Initialisation indispensable de toute l'application. */
export function ChargementPleinEcran({ texte = 'Préparation de votre espace…' }) {
  return (
    <div className="fixed inset-0 z-[150] grid place-items-center bg-[var(--bg-main,#F3F6F4)]" role="status" aria-live="polite">
      <div className="text-center">
        <Loader2 className="w-9 h-9 animate-spin mx-auto text-[var(--accent-texte)]" aria-hidden="true" />
        <p className="mt-3 text-[13.5px] text-[var(--text-muted)]">{texte}</p>
      </div>
    </div>
  );
}

/** Données déjà affichées en cours d'actualisation — on ne les efface pas. */
export function IndicateurActualisation({ actif, majLe, onActualiser }) {
  if (!actif && !onActualiser) return null;
  const quand = majLe ? new Date(majLe).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : null;
  return (
    <span className="inline-flex items-center gap-1.5 text-[12px] text-[var(--text-muted)]" role="status" aria-live="polite">
      {onActualiser ? (
        <button type="button" onClick={onActualiser} disabled={actif}
                className="inline-flex items-center gap-1.5 hover:text-[var(--text-bold)] disabled:cursor-progress"
                title="Actualiser">
          <RefreshCw className={`w-3.5 h-3.5 ${actif ? 'animate-spin' : ''}`} aria-hidden="true" />
          {actif ? 'Actualisation…' : quand ? `À jour à ${quand}` : 'Actualiser'}
        </button>
      ) : (
        <><RefreshCw className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />Actualisation…</>
      )}
    </span>
  );
}

/** Le trait de navigation entre pages, en haut de l'écran. */
export function BarreNavigation() {
  const { pathname } = useLocation();
  const [largeur, setLargeur] = useState(0);
  const premier = useRef(true);
  useEffect(() => {
    if (premier.current) { premier.current = false; return undefined; }
    setLargeur(30);
    const t1 = setTimeout(() => setLargeur(80), 120);
    const t2 = setTimeout(() => setLargeur(100), 320);
    const t3 = setTimeout(() => setLargeur(0), 650);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [pathname]);
  return <div className="barre-navigation" aria-hidden="true" style={{ width: `${largeur}%`, opacity: largeur && largeur < 100 ? 1 : 0 }} />;
}
